import { mediaUrl, type Post } from './api'
import siteRoutes from '@shared/site-routes.json'

// The server injects these same tags into the HTML of every request (see
// server/src/site); this module only keeps them current on client-side
// navigation.

export const SITE_URL = 'https://atzsolutionllc.com'
const DEFAULT_ROBOTS = 'index, follow, max-snippet:-1, max-video-preview:-1, max-image-preview:large'

export interface PageMeta {
  title: string
  description: string
  ogTitle?: string
  ogDescription?: string
  ogType?: string
  ogImage?: string
  ogImageAlt?: string
  twitterCard?: string
  twitterTitle?: string
  twitterDescription?: string
  twitterImage?: string
  robots?: string
  canonical?: string
}

const ROUTE_META = siteRoutes.pages as Record<string, PageMeta>

function setTag(attr: 'name' | 'property', key: string, content: string | undefined) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!content) {
    el?.remove()
    return
  }
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

const absolute = (url: string) => (url.startsWith('http') ? url : `${SITE_URL}${url}`)

const imageType = (url: string) =>
  /\.png($|\?)/i.test(url) ? 'image/png' : /\.webp($|\?)/i.test(url) ? 'image/webp' : 'image/jpeg'

export function applyMeta(meta: PageMeta, canonicalPath: string) {
  const canonical = meta.canonical || `${SITE_URL}${canonicalPath}`
  const ogTitle = meta.ogTitle || meta.title
  const ogDescription = meta.ogDescription || meta.description
  const ogImage = meta.ogImage ? absolute(meta.ogImage) : undefined
  const isStaticOg = !!meta.ogImage?.startsWith('/og/')

  document.title = meta.title
  setTag('name', 'description', meta.description)
  setTag('name', 'robots', meta.robots || DEFAULT_ROBOTS)

  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'canonical'
    document.head.appendChild(link)
  }
  link.href = canonical

  setTag('property', 'og:type', meta.ogType || 'website')
  setTag('property', 'og:title', ogTitle)
  setTag('property', 'og:description', ogDescription)
  setTag('property', 'og:url', canonical)
  setTag('property', 'og:image', ogImage)
  setTag('property', 'og:image:secure_url', ogImage)
  setTag('property', 'og:image:type', ogImage && imageType(ogImage))
  setTag('property', 'og:image:width', isStaticOg ? '1200' : undefined)
  setTag('property', 'og:image:height', isStaticOg ? '630' : undefined)
  setTag('property', 'og:image:alt', ogImage && (meta.ogImageAlt || ogTitle))

  setTag('name', 'twitter:card', meta.twitterCard || 'summary_large_image')
  setTag('name', 'twitter:title', meta.twitterTitle || ogTitle)
  setTag('name', 'twitter:description', meta.twitterDescription || ogDescription)
  setTag('name', 'twitter:image', meta.twitterImage ? absolute(meta.twitterImage) : ogImage)
}

export const NOT_FOUND_META: PageMeta = {
  title: 'Page Not Found | ATZ Solution LLC',
  description: 'The page you are looking for could not be found.',
  robots: 'noindex, follow',
}

// Called by Layout on every route change. Blog posts and the 404 page are
// skipped here — they apply their own meta.
export function applyRouteMeta(pathname: string) {
  const meta = ROUTE_META[pathname]
  if (meta) applyMeta(meta, pathname)
}

export const isStaticRoute = (pathname: string) => pathname in ROUTE_META

// Mirrors postMeta() in server/src/site/meta.ts (fallback chains from the
// blog editor spec).
export function postPageMeta(post: Post): PageMeta {
  const seo = post.seo || {}
  const description =
    seo.metaDescription || (seo.useExcerptAsDescription !== false ? post.excerpt : '') || ''
  const ogTitle = seo.ogTitle || seo.metaTitle || post.title
  const ogMedia = seo.ogImage || post.coverImage
  const indexable = seo.index !== false
  return {
    title: seo.metaTitle || `${post.title} | ATZ Solution LLC`,
    description,
    robots: [
      indexable ? 'index' : 'noindex',
      seo.follow === false ? 'nofollow' : 'follow',
      ...(indexable ? ['max-snippet:-1', 'max-video-preview:-1', 'max-image-preview:large'] : []),
    ].join(', '),
    canonical: seo.canonicalMode === 'manual' && seo.canonicalUrl ? seo.canonicalUrl : undefined,
    ogType: 'article',
    ogTitle,
    ogDescription: seo.ogDescription || description,
    ogImage: ogMedia ? mediaUrl(ogMedia, 'hero') : undefined,
    ogImageAlt: ogMedia?.alt || ogTitle,
    twitterCard: seo.twitterCard,
    twitterTitle: seo.twitterTitle,
    twitterDescription: seo.twitterDescription,
    twitterImage: seo.twitterImage ? mediaUrl(seo.twitterImage, 'hero') : undefined,
  }
}
